// Hoa hồng — CTV (GĐ8, §2.5, §5.3). Bậc lũy tiến đếm lại MỖI NGÀY theo ngày tạo đơn của các shop
// CTV giới thiệu: hôm nay ("51 đơn · ước tính 15.300đ · còn 50 đơn nữa lên mức 500đ/đơn"), bảng
// bậc (tô bậc hôm nay đang ở), từng ngày trong tháng, từng đơn đã ghi, lịch sử theo tháng.
// Hoa hồng có khi BEST lấy hàng thành công (giao / hoàn / thất lạc đều giữ), cộng dồn, chốt theo tháng.

import { useState } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { get } from '@/lib/api'
import { money } from '@/lib/format'
import { cn } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { LoadError } from '@/components/ui/data-notice'
import { Section } from '@/components/ui/section'
import { Skeleton } from '@/components/ui/skeleton'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { CommissionLinesTable, MonthStepper, ProgressBar, TierTable } from '@/components/commissions/parts'
import {
  MODE_SHORT,
  MONTH_STATUS_LABEL,
  currentMonthVN,
  currentTierIndex,
  monthLabel,
  tierFraction,
  vnd,
  type CommissionDay,
  type CommissionLine,
  type MyCommission,
  type MyCommissionMonth,
} from '@/components/commissions/types'
import { getAuthState } from '@/lib/auth'
import { useNppName } from '@/components/layout/shell-model'

export default function CtvCommissionsPage() {
  const nowMonth = currentMonthVN()
  const [month, setMonth] = useState(nowMonth)
  const selfId = getAuthState().activeShopId ?? undefined

  const meQ = useQuery({ queryKey: ['commissions', 'me', month], queryFn: () => get<MyCommission>(`/commissions/me?month=${month}`) })
  const linesQ = useQuery({ queryKey: ['commissions', 'me-lines', month], queryFn: () => get<{ lines: CommissionLine[] }>(`/commissions/me/lines?month=${month}`) })
  const monthsQ = useQuery({ queryKey: ['commissions', 'me-months'], queryFn: () => get<{ months: MyCommissionMonth[] }>('/commissions/me/months') })

  const monthColumns: DataTableColumn<MyCommissionMonth>[] = [
    {
      key: 'month',
      header: 'Tháng',
      priority: 'primary',
      cell: (m) => (
        <span className="block">
          <span className="block font-medium text-ink">Tháng {monthLabel(m.month)}</span>
          <span className="block font-mono text-xs text-muted">{m.code}</span>
        </span>
      ),
    },
    { key: 'orders', header: 'Số đơn', align: 'right', cell: (m) => <span className="tabular-nums">{m.totalOrders.toLocaleString('vi-VN')}</span> },
    { key: 'amount', header: 'Chiết khấu', align: 'right', cell: (m) => <span className="whitespace-nowrap font-mono font-semibold tabular-nums text-ink">{money(m.amount)}</span> },
    { key: 'mode', header: 'Cách tính', cell: (m) => <span className="text-inkSoft">{MODE_SHORT[m.mode] ?? m.mode}</span> },
    { key: 'payer', header: 'Người trả', cell: (m) => <span className="text-inkSoft">{m.payer}</span> },
    {
      key: 'status',
      header: 'Trạng thái',
      cell: (m) => (
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', m.status === 'closed' ? 'bg-ok/12 text-ok' : 'bg-info/12 text-info')}>
          {MONTH_STATUS_LABEL[m.status] ?? m.status}
        </span>
      ),
    },
  ]

  const dayColumns: DataTableColumn<CommissionDay>[] = [
    { key: 'day', header: 'Ngày tạo đơn', priority: 'primary', cell: (d) => <span className="font-medium text-ink">{dayLabel(d.day)}</span> },
    { key: 'orders', header: 'Số đơn', align: 'right', cell: (d) => <span className="tabular-nums">{d.orders.toLocaleString('vi-VN')}</span> },
    { key: 'pickedUp', header: 'Đã lấy hàng', align: 'right', cell: (d) => <span className="tabular-nums">{d.pickedUp.toLocaleString('vi-VN')}</span> },
    { key: 'estimated', header: 'Ước tính', align: 'right', cell: (d) => <span className="whitespace-nowrap font-mono tabular-nums text-inkSoft">{money(d.estimated)}</span> },
    { key: 'credited', header: 'Đã tính', align: 'right', cell: (d) => <span className="whitespace-nowrap font-mono font-semibold tabular-nums text-ink">{money(d.credited)}</span> },
  ]

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <PageHeader title="Chiết khấu" actions={<MonthStepper value={month} onChange={setMonth} max={nowMonth} />} />

      <ProgressCard q={meQ} month={month} isNow={month === nowMonth} />

      <Section title={`Theo ngày · tháng ${monthLabel(month)}`}>
        {meQ.isError ? (
          <LoadError onRetry={() => meQ.refetch()}>Không tải được số liệu theo ngày.</LoadError>
        ) : (
          <DataTable
            columns={dayColumns}
            data={meQ.data?.days ?? []}
            rowKey={(d) => d.day}
            loading={meQ.isLoading}
            emptyLabel="Chưa có đơn"
          />
        )}
      </Section>

      <Section title={`Đơn được tính chiết khấu · tháng ${monthLabel(month)}`}>
        {linesQ.isError ? (
          <LoadError onRetry={() => linesQ.refetch()}>Không tải được các đơn tính chiết khấu.</LoadError>
        ) : (
          <CommissionLinesTable lines={linesQ.data?.lines ?? []} loading={linesQ.isLoading} ctvShopId={selfId} />
        )}
      </Section>

      <Section title="Lịch sử theo tháng">
        {monthsQ.isError ? (
          <LoadError onRetry={() => monthsQ.refetch()}>Không tải được lịch sử chiết khấu.</LoadError>
        ) : (
          <DataTable
            columns={monthColumns}
            data={monthsQ.data?.months ?? []}
            rowKey={(m) => m.code}
            loading={monthsQ.isLoading}
            onRowClick={(m) => setMonth(m.month)}
            emptyLabel="Chưa có chiết khấu"
          />
        )}
      </Section>
    </div>
  )
}

function ProgressCard({ q, month, isNow }: { q: UseQueryResult<MyCommission>; month: string; isNow: boolean }) {
  const nppName = useNppName()
  if (q.isError) {
    return (
      <Section>
        <LoadError onRetry={() => q.refetch()}>Không tải được chiết khấu tháng {monthLabel(month)}.</LoadError>
      </Section>
    )
  }
  const d = q.data
  if (q.isLoading || !d) {
    return (
      <section className="space-y-3 rounded-2xl bg-band p-5 sm:p-6">
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </section>
    )
  }

  const tiers = d.def?.tiers ?? []
  const today = isNow ? d.today : null
  const todayOrders = today?.orders ?? 0
  const idx = d.def && today ? currentTierIndex(tiers, todayOrders) : null
  const p = d.progress
  const closed = d.status === 'closed'
  const empty = d.status === 'empty' || (!isNow && d.days.length === 0 && d.totalOrders === 0)
  const pending = Math.max(0, d.estimated - d.accrued)

  return (
    <section className="space-y-5 rounded-2xl bg-band p-5 sm:p-6">
      <div className="space-y-3">
        <p className="text-[13px] font-medium text-inkSoft">
          {d.scheme ? (
            <>
              {d.scheme.name} <span className="font-mono text-xs text-muted">{d.scheme.code}</span> · {d.scheme.payerKey === 'platform' ? `${nppName} trả` : 'Đại lý trả'} ·{' '}
              {closed ? 'Đã chốt' : empty ? 'Không phát sinh' : 'Tạm tính'}
            </>
          ) : (
            'Chưa có bảng chiết khấu'
          )}
        </p>
        <h2 className="!text-[1.35rem] leading-snug text-ink mobile:!text-[1.15rem]">
          Tháng {monthLabel(month)}: đã tính <span className="tabular-nums text-accent">{vnd(d.accrued)}</span> ·{' '}
          <span className="tabular-nums">{d.totalOrders.toLocaleString('vi-VN')}</span> đơn đã lấy hàng
        </h2>
        {!empty && pending > 0 && !closed && (
          <p className="text-sm text-inkSoft">
            Ước tính cả tháng <strong className="tabular-nums text-ink">{vnd(d.estimated)}</strong> — còn {vnd(pending)} chờ hãng lấy hàng.
          </p>
        )}
      </div>

      {today && d.def && (
        <div className="space-y-2.5 rounded-xl bg-raise p-4">
          <p className="text-[13px] font-medium text-inkSoft">Hôm nay {dayLabel(today.day)}</p>
          <p className="text-base leading-snug text-ink">
            <span className="tabular-nums font-semibold">{todayOrders.toLocaleString('vi-VN')}</span> đơn · ước tính{' '}
            <span className="tabular-nums font-semibold text-accent">{vnd(today.estimated)}</span>
            {p && p.nextRate != null && p.ordersToNext != null && (
              <>
                {' '}
                · còn <span className="tabular-nums">{p.ordersToNext.toLocaleString('vi-VN')}</span> đơn nữa lên mức {vnd(p.nextRate)}/đơn
              </>
            )}
            {p && p.nextRate == null && todayOrders > 0 && <> · đang ở mức cao nhất {vnd(p.currentRate)}/đơn</>}
          </p>
          <ProgressBar value={tierFraction(tiers, todayOrders)} label="Tiến độ lên bậc" className="h-2.5 bg-paperSoft" />
          {today.pickedUp > 0 && (
            <p className="text-xs text-muted">
              Hãng đã lấy {today.pickedUp.toLocaleString('vi-VN')}/{todayOrders.toLocaleString('vi-VN')} đơn — đã tính {vnd(today.credited)}
            </p>
          )}
        </div>
      )}

      {d.def && (
        <div className="space-y-1.5">
          <TierTable tiers={tiers} currentIndex={idx} currentLabel="Hôm nay" className="bg-raise" />
        </div>
      )}
    </section>
  )
}

/** "2026-10-03" → "03/10" */
function dayLabel(day: string): string {
  const [, m, dd] = day.split('-')
  return m && dd ? `${dd}/${m}` : day
}
